import { type DocumentData, type FieldValue } from 'firebase/firestore';

/**
 * Converts nested objects into dot notated objects. This is necessary if running an update function in Firestore
 * in documents with nested objects.
 *
 * @param object - The object to convert to a dot-notated object.
 * @param forUpdate - Optional. Whether to format the data for use with the updateDoc method. Defaults to false.
 * @returns The dot-notated object, or an object with field-value pairs ready to be passed to the updateDoc method.
 */
export const convertObjectToDotNotatedObjectProperties = <T extends DocumentData>(
  object: T,
  forUpdate = false,
): T extends Array<infer U>
  ? Array<Record<string, FieldValue | Partial<unknown> | undefined>>
  : Record<string, FieldValue | Partial<unknown> | undefined> => {
  const dotNotatedObject: DocumentData = {};

  const recurse = (obj: Record<string, unknown>, currentPath: string) => {
    for (const [key, value] of Object.entries(obj)) {
      const newPath = currentPath ? `${currentPath}.${key}` : key;

      if (typeof value === 'object' && value !== null && value.constructor === Object) {
        recurse(value as Record<string, unknown>, newPath);
      } else {
        dotNotatedObject[newPath] = value as FieldValue | Partial<unknown> | undefined;
      }
    }
  };

  recurse(object, '');

  if (forUpdate) {
    const updateData: Record<string, FieldValue | Partial<unknown> | undefined> = {};
    for (const [key, value] of Object.entries(dotNotatedObject)) {
      updateData[key] = value as FieldValue | Partial<unknown> | undefined;
    }
    return updateData as T extends Array<infer U>
      ? Array<Record<string, FieldValue | Partial<unknown> | undefined>>
      : Record<string, FieldValue | Partial<unknown> | undefined>;
  }

  return dotNotatedObject as T extends Array<infer U>
    ? Array<Record<string, FieldValue | Partial<unknown> | undefined>>
    : Record<string, FieldValue | Partial<unknown> | undefined>;
};
